<template>
  <div>
    <BaseToolBar
      title="Entretiens"
      subtitle="Historique des entretiens et contrôles du véhicule"
    >
      <BaseButton
        class="btn btn-primary ml-3"
        v-tooltip="'Ajouter un entretien'"
        @click="addEntretien"
        :loading="loading"
        icon="plus"
        text="Ajouter"
      ></BaseButton>
    </BaseToolBar>

    <!-- Feedback message -->
    <div v-if="feedback.message" :class="`alert alert-${feedback.type} mt-3`">
      {{ feedback.message }}
    </div>

    <!-- Liste des entretiens -->
    <div
      v-for="(entretien, index) in vehicule.entretiens"
      :key="entretien.id || 'new_' + index"
      class="card mb-3"
      :class="{ 'border-danger': isEntretienExpired(entretien) }"
    >
      <div
        class="card-header d-flex justify-content-between align-items-center"
        :class="{ 'bg-danger text-white': isEntretienExpired(entretien) }"
      >
        <h6 class="mb-0">
          {{ getTypeEntretienLabel(entretien.type) }} {{ index + 1 }}
          <span :class="getBadgeClassByType(entretien.type)" class="badge ml-2">
            {{ getTypeEntretienLabel(entretien.type) }}
          </span>
          <span
            v-if="entretien.date_realisation"
            class="badge badge-secondary ml-2"
          >
            {{ formatDate(entretien.date_realisation) }}
          </span>
          <span
            v-if="entretien.date_fin_validite"
            :class="[
              'badge ml-2',
              isEntretienExpired(entretien) ? 'badge-danger' : 'badge-success'
            ]"
            v-tooltip="
              isEntretienExpired(entretien) ? 'Validité expirée' : 'Valide'
            "
          >
            {{ isEntretienExpired(entretien) ? "Expiré" : "Valide" }} jusqu'au
            {{ formatDate(entretien.date_fin_validite) }}
          </span>
        </h6>
        <div>
          <ButtonCircle
            v-if="
              !entretienEnCoursEdition ||
                entretienEnCoursEdition.id !== entretien.id
            "
            name="edit"
            @click="editEntretien(entretien)"
            class="m-1"
            :loading="loading"
            v-tooltip="'Modifier'"
          ></ButtonCircle>
          <ButtonCircle
            name="trash"
            @click="deleteEntretien(entretien)"
            class="m-1"
            :loading="loading"
            v-tooltip="'Supprimer'"
          ></ButtonCircle>
        </div>
      </div>
      <div class="card-body">
        <!-- Vue édition -->
        <div
          v-if="
            entretienEnCoursEdition &&
              entretienEnCoursEdition.id === entretien.id
          "
        >
          <base-form-row row>
            <base-select
              v-model.number="entretienEnCoursEdition.type"
              inputText="Type d'intervention"
              :options="typesEntretien"
            ></base-select>
            <base-inputDatePicker
              v-model="entretienEnCoursEdition.date_realisation"
              inputText="Date de réalisation"
            ></base-inputDatePicker>
          </base-form-row>
          <base-form-row row>
            <base-inputDatePicker
              v-model="entretienEnCoursEdition.date_fin_validite"
              inputText="Date de fin de validité"
            ></base-inputDatePicker>
          </base-form-row>
          <base-form-row>
            <base-input
              v-model="entretienEnCoursEdition.commentaires"
              inputText="Commentaires"
              type="textarea"
              rows="3"
            ></base-input>
          </base-form-row>

          <div class="d-flex justify-content-end mt-3">
            <button
              class="btn btn-secondary mr-2"
              @click="cancelEdit()"
              :disabled="loading"
            >
              Annuler
            </button>
            <button
              class="btn btn-primary"
              @click="saveEntretienEnCours()"
              :disabled="loading"
            >
              <i class="fas fa-save mr-1"></i>
              Enregistrer
            </button>
          </div>
        </div>

        <!-- Vue normale (non-édition) -->
        <div v-else>
          <div class="row mb-3">
            <div class="col-md-6">
              <b>Type:</b> {{ getTypeEntretienLabel(entretien.type) }}
            </div>
            <div class="col-md-6">
              <b>Date de réalisation:</b>
              {{
                entretien.date_realisation
                  ? formatDate(entretien.date_realisation)
                  : "Non définie"
              }}
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-md-6">
              <b>Date de fin de validité:</b>
              {{
                entretien.date_fin_validite
                  ? formatDate(entretien.date_fin_validite)
                  : "Non définie"
              }}
            </div>
            <div class="col-md-6" v-if="entretien.date_fin_validite">
              <b>Statut:</b>
              <span
                :class="[
                  isEntretienExpired(entretien) ? 'text-danger' : 'text-success'
                ]"
              >
                {{ isEntretienExpired(entretien) ? "Expiré" : "Valide" }}
                <i
                  :class="[
                    isEntretienExpired(entretien)
                      ? 'fas fa-exclamation-triangle'
                      : 'fas fa-check-circle'
                  ]"
                ></i>
              </span>
            </div>
          </div>
          <div class="row mb-3" v-if="entretien.commentaires">
            <div class="col-md-12">
              <b>Commentaires:</b> {{ entretien.commentaires }}
            </div>
          </div>
        </div>

        <div class="pt-3" v-if="entretien.id">
          <uploader
            :id="'doc_vehicule_entretien_' + entretien.id"
            url="/upload/document"
            file_system="VEHICULES_ENTRETIENS"
            file_field="entretien"
            :file_key="entretien.id"
            title="Pièces jointes"
          ></uploader>
        </div>
      </div>
    </div>

    <div
      v-if="!vehicule.entretiens || vehicule.entretiens.length === 0"
      class="alert alert-info"
    >
      Ce véhicule n'a pas encore d'entretien enregistré. Cliquez sur "Ajouter"
      pour ajouter un entretien ou contrôle.
    </div>

    <!-- Tableau récapitulatif -->
    <div class="mt-4">
      <h5>Récapitulatif</h5>
      <table class="table table-bordered table-hover">
        <thead class="thead-light">
          <tr>
            <th>Type</th>
            <th>Dernier</th>
            <th>Fin de validité</th>
            <th>Statut</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="type in typesEntretien" :key="type.id">
            <td>{{ type.description }}</td>
            <td>
              {{ getLatestEntretienDate(type.id) || "Aucun" }}
            </td>
            <td>
              {{ getLatestEntretienValiditeDate(type.id) || "Non définie" }}
            </td>
            <td>
              <span
                v-if="getLatestEntretienValiditeDate(type.id)"
                :class="[
                  isTypeExpired(type.id) ? 'text-danger' : 'text-success'
                ]"
              >
                <i
                  :class="[
                    isTypeExpired(type.id)
                      ? 'fas fa-exclamation-triangle'
                      : 'fas fa-check-circle'
                  ]"
                ></i>
                {{ isTypeExpired(type.id) ? "Expiré" : "Valide" }}
              </span>
              <span v-else class="text-muted">
                Non applicable
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="d-flex justify-content-between mt-4">
      <div class="card text-white bg-info mb-3" style="max-width: 18rem;">
        <div class="card-header">Statistiques</div>
        <div class="card-body">
          <h5 class="card-title">
            {{ vehicule.entretiens ? vehicule.entretiens.length : 0 }}
            intervention(s)
          </h5>
          <p class="card-text">
            Entretiens: {{ countEntretienByType(0) }}<br />
            Audits: {{ countEntretienByType(1) }}<br />
            Contrôles techniques: {{ countEntretienByType(2) }}
          </p>
        </div>
      </div>

      <div
        v-if="hasExpiredEntretiens()"
        class="card text-white bg-danger mb-3"
        style="max-width: 24rem;"
      >
        <div class="card-header">
          <i class="fas fa-exclamation-triangle mr-1"></i>
          Attention
        </div>
        <div class="card-body">
          <h5 class="card-title">
            Interventions expirées
          </h5>
          <p class="card-text">
            Ce véhicule possède {{ countExpiredEntretiens() }} intervention(s)
            dont la validité est expirée.
            <br />
            Veuillez planifier les interventions nécessaires.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { errHandler } from "@/helpers.js";
import BaseToolBar from "@/components/bases/ToolBar.vue";
import BaseFormRow from "@/components/bases/FormRow.vue";
import BaseInput from "@/components/bases/Input.vue";
import BaseSelect from "@/components/bases/Select.vue";
import BaseInputDatePicker from "@/components/bases/InputDatePicker.vue";
import BaseButton from "@/components/bases/Button.vue";
import ButtonCircle from "@/components/bases/ButtonCircle.vue";
import Uploader from "@/components/bases/Uploader.vue";

export default {
  name: "VehiculeEntretien",
  components: {
    BaseToolBar,
    BaseFormRow,
    BaseInput,
    BaseSelect,
    BaseInputDatePicker,
    BaseButton,
    ButtonCircle,
    Uploader
  },
  props: {
    vehicule: {
      type: Object,
      required: true
    },
    statut: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      entretienEnCoursEdition: null,
      isLoading: false,
      feedback: {
        message: "",
        type: "info"
      },
      typesEntretien: [
        { id: 0, description: "Entretien" },
        { id: 1, description: "Audit" },
        { id: 2, description: "Contrôle technique" }
      ]
    };
  },
  mounted() {
    // Initialiser la liste des entretiens si elle n'existe pas
    if (!this.vehicule.entretiens) {
      this.$set(this.vehicule, "entretiens", []);
    }
  },
  methods: {
    addEntretien() {
      // Vérifier que le véhicule est bien enregistré
      if (!this.vehicule.id) {
        this.showFeedback(
          "Veuillez d'abord enregistrer le véhicule",
          "warning"
        );
        return;
      }

      // Fermer toute édition en cours
      this.entretienEnCoursEdition = null;

      // Créer immédiatement un entretien en base de données
      this.isLoading = true;

      this.$http
        .post("/vehicules-entretien", { vehicule_id: this.vehicule.id })
        .then(response => {
          // Vérifier que entretiens est initialisé
          if (!this.vehicule.entretiens) {
            this.$set(this.vehicule, "entretiens", []);
          }

          // Ajouter l'entretien retourné par l'API
          this.vehicule.entretiens.unshift(response.data);

          // Passer en mode édition sur ce nouvel entretien
          this.editEntretien(response.data);

          this.showFeedback(
            "Intervention créée avec succès. Vous pouvez maintenant compléter les détails.",
            "success"
          );
        })
        .catch(error => {
          console.error(error);
          this.showFeedback(errHandler(error), "danger");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    editEntretien(entretien) {
      // Créer une copie pour ne pas modifier directement l'original
      this.entretienEnCoursEdition = { ...entretien };
    },
    cancelEdit() {
      this.entretienEnCoursEdition = null;
    },
    saveEntretienEnCours() {
      if (!this.entretienEnCoursEdition || !this.entretienEnCoursEdition.id)
        return;

      this.isLoading = true;
      this.$http
        .put(
          "/vehicules-entretien/" + this.entretienEnCoursEdition.id,
          this.entretienEnCoursEdition
        )
        .then(response => {
          // Mettre à jour l'entretien dans le tableau
          const index = this.vehicule.entretiens.findIndex(
            e => e.id === this.entretienEnCoursEdition.id
          );
          if (index !== -1) {
            this.$set(this.vehicule.entretiens, index, response.data);
          }
          this.entretienEnCoursEdition = null;
          this.showFeedback("Intervention mise à jour avec succès", "success");
        })
        .catch(error => {
          console.error(error);
          this.showFeedback(errHandler(error), "danger");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    deleteEntretien(entretien) {
      if (
        !window.confirm(
          "Êtes-vous sûr de vouloir supprimer cette intervention ?"
        )
      ) {
        return;
      }

      // Si c'est un nouvel entretien non encore sauvegardé
      if (!entretien.id) {
        const index = this.vehicule.entretiens.findIndex(e => e === entretien);
        if (index !== -1) {
          this.vehicule.entretiens.splice(index, 1);
        }
        return;
      }

      this.isLoading = true;
      this.$http
        .delete(`/vehicules-entretien/${entretien.id}`)
        .then(() => {
          const index = this.vehicule.entretiens.findIndex(
            e => e.id === entretien.id
          );
          if (index !== -1) {
            this.vehicule.entretiens.splice(index, 1);
          }
          this.showFeedback("Intervention supprimée avec succès", "success");
        })
        .catch(error => {
          console.error(error);
          this.showFeedback(errHandler(error), "danger");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getTypeEntretienLabel(typeId) {
      const type = this.typesEntretien.find(t => t.id === parseInt(typeId));
      return type ? type.description : "Non défini";
    },
    countEntretienByType(typeId) {
      if (!this.vehicule.entretiens) return 0;
      return this.vehicule.entretiens.filter(e => parseInt(e.type) === typeId)
        .length;
    },
    formatDate(date) {
      if (!date) return "";
      return this.$moment(date).format("DD/MM/YYYY");
    },
    isEntretienExpired(entretien) {
      if (!entretien.date_fin_validite) return false;
      return this.$moment().isAfter(this.$moment(entretien.date_fin_validite));
    },
    hasExpiredEntretiens() {
      if (!this.vehicule.entretiens || this.vehicule.entretiens.length === 0)
        return false;
      return this.vehicule.entretiens.some(entretien =>
        this.isEntretienExpired(entretien)
      );
    },
    countExpiredEntretiens() {
      if (!this.vehicule.entretiens) return 0;
      return this.vehicule.entretiens.filter(entretien =>
        this.isEntretienExpired(entretien)
      ).length;
    },
    getLatestEntretienDate(typeId) {
      if (!this.vehicule.entretiens || this.vehicule.entretiens.length === 0)
        return null;

      const typeEntretiens = this.vehicule.entretiens.filter(
        e => parseInt(e.type) === typeId && e.date_realisation
      );

      if (typeEntretiens.length === 0) return null;

      // Trouver l'entretien avec la date la plus récente
      const latestEntretien = typeEntretiens.reduce((latest, current) => {
        return this.$moment(current.date_realisation).isAfter(
          this.$moment(latest.date_realisation)
        )
          ? current
          : latest;
      }, typeEntretiens[0]);

      return this.formatDate(latestEntretien.date_realisation);
    },
    getLatestEntretienValiditeDate(typeId) {
      if (!this.vehicule.entretiens || this.vehicule.entretiens.length === 0)
        return null;

      const typeEntretiens = this.vehicule.entretiens.filter(
        e =>
          parseInt(e.type) === typeId &&
          e.date_realisation &&
          e.date_fin_validite
      );

      if (typeEntretiens.length === 0) return null;

      // Trouver l'entretien avec la date de fin de validité la plus récente
      const latestEntretien = typeEntretiens.reduce((latest, current) => {
        return this.$moment(current.date_fin_validite).isAfter(
          this.$moment(latest.date_fin_validite)
        )
          ? current
          : latest;
      }, typeEntretiens[0]);

      return this.formatDate(latestEntretien.date_fin_validite);
    },
    isTypeExpired(typeId) {
      if (!this.vehicule.entretiens || this.vehicule.entretiens.length === 0)
        return false;

      const typeEntretiens = this.vehicule.entretiens.filter(
        e => parseInt(e.type) === typeId && e.date_fin_validite
      );

      if (typeEntretiens.length === 0) return false;

      // Trouver l'entretien avec la date de fin de validité la plus récente
      const latestEntretien = typeEntretiens.reduce((latest, current) => {
        return this.$moment(current.date_fin_validite).isAfter(
          this.$moment(latest.date_fin_validite)
        )
          ? current
          : latest;
      }, typeEntretiens[0]);

      // Vérifier si cette date est expirée
      return this.$moment().isAfter(
        this.$moment(latestEntretien.date_fin_validite)
      );
    },
    getBadgeClassByType(typeId) {
      typeId = parseInt(typeId);
      switch (typeId) {
        case 0:
          return "badge-primary"; // Entretien
        case 1:
          return "badge-info"; // Audit
        case 2:
          return "badge-warning"; // Contrôle technique
        default:
          return "badge-secondary";
      }
    },
    showFeedback(message, type = "info") {
      this.feedback = { message, type };
      // Faire disparaître le message après quelques secondes
      setTimeout(() => {
        this.feedback = { message: "", type: "info" };
      }, 3000);
    }
  }
};
</script>

<style scoped>
.card-header {
  background-color: #f8f9fa;
}

.badge-primary {
  background-color: #007bff;
  color: #fff;
}

.badge-info {
  background-color: #17a2b8;
  color: #fff;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-secondary {
  background-color: #6c757d;
  color: #fff;
}

.badge-danger {
  background-color: #dc3545;
  color: #fff;
}

.badge-success {
  background-color: #28a745;
  color: #fff;
}

.text-danger {
  color: #dc3545 !important;
}

.text-success {
  color: #28a745 !important;
}

.border-danger {
  border-color: #dc3545 !important;
}

.bg-danger {
  background-color: #dc3545 !important;
}

.thead-light th {
  background-color: #f8f9fa;
  border-color: #dee2e6;
}
</style>
